import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import api from "@/lib/api";
import { ArrowLeft } from "lucide-react";

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

export default function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/blog/${slug}`).then(({ data }) => setPost(data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="bg-background min-h-screen" data-testid="blog-notfound">
        <Navbar />
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 pt-48 pb-32 text-center">
          <h1 className="font-display text-5xl mb-6">Article not found</h1>
          <button onClick={() => navigate("/blog")} className="font-mono2 text-xs uppercase tracking-[0.2em] link-underline" data-testid="back-to-journal">
            ← Back to Journal
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="bg-background min-h-screen">
        <Navbar />
        <p className="font-mono2 text-sm text-muted-foreground text-center pt-48">Loading…</p>
      </div>
    );
  }

  return (
    <div data-testid="blog-post-page" className="bg-background">
      <Navbar />
      <article>
        <div className="relative h-[60vh] min-h-[420px] overflow-hidden">
          <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-foreground/55" />
          <div className="absolute inset-0 flex items-end">
            <div className="mx-auto max-w-[1000px] w-full px-5 md:px-10 pb-14">
              <div className="flex items-center gap-3 font-mono2 text-[11px] uppercase tracking-[0.2em] text-background/80 mb-5">
                <span className="bg-primary text-primary-foreground px-3 py-1">{post.category}</span>
                <span>{fmtDate(post.created_at)}</span>
              </div>
              <h1 className="font-display text-background text-4xl md:text-6xl leading-[1.0] max-w-3xl">{post.title}</h1>
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-[760px] px-5 md:px-10 py-16 md:py-24">
          <Link to="/blog" className="font-mono2 text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2 link-underline mb-12" data-testid="back-link">
            <ArrowLeft size={14} /> All articles
          </Link>
          <p className="text-xl md:text-2xl font-display italic text-foreground/80 leading-snug mb-10 border-l-2 border-primary pl-6">
            {post.excerpt}
          </p>
          <div className="prose-content space-y-6 text-lg leading-relaxed text-foreground/85" data-testid="post-content">
            {post.content.split("\n\n").map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
          <div className="mt-16 pt-8 border-t border-border font-mono2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
            By {post.author}
          </div>
        </div>
      </article>
      <Footer />
    </div>
  );
}
